import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { contactPrivacyCopy } from '@/content/resultCopy';
import {
  CONTACT_LIMITS,
  buildSubmission,
  contactMethodMeta,
  emptyContactForm,
  validateContactForm,
  type ContactErrors,
  type ContactFormValues,
  type ContactMethod,
} from '@/domain/contact';
import type { CompatibilityResult, Profile, Question, VisitorAnswer } from '@/domain/types';
import { track } from '@/lib/analytics';
import { submitContact } from '@/lib/api';
import { getSource } from '@/lib/source';
import { clearJourney } from '@/lib/storage';
import styles from './ContactForm.module.css';

/**
 * The same form on both routes.
 *
 * From the result page it carries a result and a set of answers; from
 * `/:slug/contact` it carries neither, because there is no journey behind it.
 * That is the only difference, and everything conditional below hangs off it:
 * no answer-sharing control with nothing to share, and no line in the privacy
 * panel about a result that does not exist.
 */
interface ContactFormProps {
  profile: Profile;
  /** Omitted on the direct route. */
  questions?: Question[];
  /** Omitted on the direct route. */
  answers?: Record<string, VisitorAnswer>;
  /** Null when she came straight from the profile. */
  result?: CompatibilityResult | null;
}

type Status = 'idle' | 'submitting' | 'sent';

const METHODS: ContactMethod[] = ['email', 'phone'];

export function ContactForm({
  profile,
  questions = [],
  answers = {},
  result = null,
}: ContactFormProps) {
  /** Whether there is a journey behind this form at all. */
  const hasJourney = result !== null && Object.keys(answers).length > 0;
  const ids = {
    firstName: useId(),
    contactValue: useId(),
    message: useId(),
    share: useId(),
    formError: useId(),
  };

  const [values, setValues] = useState<ContactFormValues>(emptyContactForm);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const successRef = useRef<HTMLDivElement | null>(null);

  const method = contactMethodMeta[values.contactMethod];

  /**
   * The form is tall and the confirmation that replaces it is short, so the
   * page collapses out from under the scroll position and she is left looking
   * at empty space. Bring the confirmation to her, and move focus onto it so
   * keyboard and screen-reader users land there too.
   *
   * Scroll behaviour is left to CSS, which already drops the smooth scroll
   * under reduced motion.
   */
  useEffect(() => {
    if (status !== 'sent') return;
    const node = successRef.current;
    if (!node) return;

    node.scrollIntoView({ block: 'center' });
    node.focus({ preventScroll: true });
  }, [status]);

  // Errors are only shown after the first submit attempt, so the form does not
  // start by telling her she has done something wrong.
  const visibleErrors: ContactErrors = submitted ? errors : {};

  const set = <K extends keyof ContactFormValues>(key: K, value: ContactFormValues[K]) => {
    setValues((current) => {
      const next = { ...current, [key]: value };
      if (submitted) setErrors(validateContactForm(next));
      return next;
    });
  };

  const payloadPreview = useMemo(
    () =>
      buildSubmission({
        profileId: profile.id,
        values,
        result,
        questions,
        answers,
        source: getSource(),
      }),
    [profile.id, values, result, questions, answers],
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setFormError(null);

    const found = validateContactForm(values);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      // Move focus to the first thing that needs attention, rather than
      // leaving her to hunt for the red text.
      const firstKey = Object.keys(found)[0];
      const targetId = Object.prototype.hasOwnProperty.call(ids, firstKey)
        ? ids[firstKey as keyof typeof ids]
        : null;
      if (targetId) document.getElementById(targetId)?.focus();
      return;
    }

    setStatus('submitting');
    const outcome = await submitContact(payloadPreview);

    if (outcome.status === 'ok') {
      setStatus('sent');
      track({
        name: 'contact_form_submitted',
        profileId: profile.id,
        category: result?.category ?? 'none',
        sharedAnswers: payloadPreview.consentToShareAnswers,
      });
      // She has sent it; there is no reason to keep her answers on the device.
      // Only when there were any: somebody who said hello from the profile may
      // have a half-finished journey saved, and erasing it would be this form
      // throwing away work it had nothing to do with.
      if (hasJourney) clearJourney(profile.id);
      return;
    }

    setStatus('idle');
    setFormError(outcome.message);
    track({ name: 'contact_form_failed', profileId: profile.id });
    window.setTimeout(() => errorRef.current?.focus(), 0);
  };

  /* --- Sent --------------------------------------------------------------- */

  if (status === 'sent') {
    return (
      <div className={styles.success} role="status" tabIndex={-1} ref={successRef}>
        <span className={styles.successMark} aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
            <path
              d="M6 13.4 11 18.4 20 8"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <h3 className={styles.successTitle}>That is on its way to {profile.displayName}.</h3>
        <p className={styles.successBody}>
          Thank you, {values.firstName.trim()}. That took a bit of nerve and it is
          appreciated.
          {hasJourney ? ' Your answers have been cleared from this device.' : ''}
        </p>
      </div>
    );
  }

  /* --- Form ---------------------------------------------------------------- */

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {/* First name */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor={ids.firstName}>
          First name
        </label>
        <input
          id={ids.firstName}
          className={`${styles.input} ${visibleErrors.firstName ? styles.inputInvalid : ''}`}
          type="text"
          autoComplete="given-name"
          value={values.firstName}
          maxLength={CONTACT_LIMITS.firstNameMax}
          onChange={(e) => set('firstName', e.target.value)}
          aria-invalid={visibleErrors.firstName ? true : undefined}
          aria-describedby={visibleErrors.firstName ? `${ids.firstName}-error` : undefined}
          required
        />
        {visibleErrors.firstName ? (
          <p id={`${ids.firstName}-error`} className={styles.error}>
            {visibleErrors.firstName}
          </p>
        ) : null}
      </div>

      {/*
        The message comes before the contact details on purpose. A reader's
        note on the old order: "for a coffee, I want to type 'Hi, enjoyed
        this — coffee next week?' and leave one contact detail." Opening with
        the admin made this read as onboarding; opening with the human part
        makes it read as a message that happens to need an address on it.
      */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor={ids.message}>
          Say something <span className={styles.optional}>(optional)</span>
        </label>
        <textarea
          id={ids.message}
          className={`${styles.textarea} ${visibleErrors.message ? styles.inputInvalid : ''}`}
          value={values.message}
          maxLength={CONTACT_LIMITS.messageMax}
          rows={4}
          placeholder="Hi James. Enjoyed this. Coffee next week?"
          onChange={(e) => set('message', e.target.value)}
          aria-invalid={visibleErrors.message ? true : undefined}
        />
        <p className={styles.counter}>
          {values.message.length} / {CONTACT_LIMITS.messageMax}
        </p>
        {visibleErrors.message ? <p className={styles.error}>{visibleErrors.message}</p> : null}
      </div>

      {/* Contact method */}
      <div className={styles.field}>
        <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.25rem' }}>
          <legend className={styles.label}>How should I reach you?</legend>
          <div className={styles.methods}>
            {METHODS.map((value) => (
              <label
                key={value}
                className={`${styles.method} ${
                  values.contactMethod === value ? styles.methodSelected : ''
                }`}
              >
                <input
                  className={styles.methodInput}
                  type="radio"
                  name="contactMethod"
                  value={value}
                  checked={values.contactMethod === value}
                  onChange={() => {
                    set('contactMethod', value);
                    set('contactValue', '');
                  }}
                />
                <span>{contactMethodMeta[value].label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      {/* Contact value */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor={ids.contactValue}>
          Your {method.label.toLowerCase()}
        </label>
        <input
          id={ids.contactValue}
          className={`${styles.input} ${visibleErrors.contactValue ? styles.inputInvalid : ''}`}
          type={method.inputType}
          inputMode={values.contactMethod === 'phone' ? 'tel' : undefined}
          autoComplete={method.autoComplete}
          placeholder={method.placeholder}
          value={values.contactValue}
          maxLength={CONTACT_LIMITS.contactValueMax}
          onChange={(e) => set('contactValue', e.target.value)}
          aria-invalid={visibleErrors.contactValue ? true : undefined}
          aria-describedby={`${ids.contactValue}-hint${
            visibleErrors.contactValue ? ` ${ids.contactValue}-error` : ''
          }`}
          required
        />
        <p id={`${ids.contactValue}-hint`} className={styles.hint}>
          {method.hint}
        </p>
        {visibleErrors.contactValue ? (
          <p id={`${ids.contactValue}-error`} className={styles.error}>
            {visibleErrors.contactValue}
          </p>
        ) : null}
      </div>

      {/* Answer sharing. Off by default, and the only real consent decision
          left on this form — the server rejects answers without it.

          Absent entirely on the direct route: offering to send answers that do
          not exist is a control that cannot do anything, and a tick box about
          privacy that does nothing is worse than no tick box at all. */}
      {hasJourney ? (
        <div className={styles.checks}>
          <label
            className={`${styles.check} ${values.shareAnswers ? styles.checkSelected : ''}`}
          >
            <input
              id={ids.share}
              className={styles.checkInput}
              type="checkbox"
              checked={values.shareAnswers}
              onChange={(e) => set('shareAnswers', e.target.checked)}
            />
            <Check />
            <span className={styles.checkText}>
              <span className={styles.checkLabel}>{contactPrivacyCopy.shareAnswersLabel}</span>
              <span className={styles.checkDetail}>
                Entirely optional. Everything else still gets sent either way.
              </span>
            </span>
          </label>
        </div>
      ) : null}

      {/*
        Closed by default. The list itself was the best-liked thing on this
        screen — the problem was only that it arrived before anyone had asked,
        which made a short form look like a data-processing agreement. Nothing
        is hidden: one tap shows the identical list, and the summary names what
        is behind it.
      */}
      <details className={styles.disclosure}>
        <summary className={styles.disclosureSummary}>{contactPrivacyCopy.heading}</summary>

        {/* The grid layout hangs off this wrapper rather than the <details>
            itself — see the note in the stylesheet. */}
        <div className={styles.disclosureBody}>
          <ul className={styles.disclosureList}>
            {contactPrivacyCopy.items.map((item) => (
              <li key={item}>
                <span className={styles.disclosureGlyph} aria-hidden="true">
                  ●
                </span>
                {item}
              </li>
            ))}

            {/* The result line only claims a result when there is one. */}
            <li className={hasJourney ? undefined : styles.disclosureOff}>
              <span className={styles.disclosureGlyph} aria-hidden="true">
                ●
              </span>
              {hasJourney
                ? contactPrivacyCopy.resultItem
                : contactPrivacyCopy.noResultItem}
            </li>

            {hasJourney ? (
              <li className={values.shareAnswers ? undefined : styles.disclosureOff}>
                <span className={styles.disclosureGlyph} aria-hidden="true">
                  ●
                </span>
                {contactPrivacyCopy.optionalItem}
              </li>
            ) : null}
          </ul>
          <p className={styles.disclosureNote}>{contactPrivacyCopy.assurance}</p>
          <p className={styles.disclosureNote}>{contactPrivacyCopy.retention}</p>
        </div>
      </details>

      {/*
        There is no "I consent to this being sent" tick any more. Pressing a
        button that says "Send to James" is the consent, and asking for it
        twice made a coffee invitation feel like a form. The value was never
        transmitted — see `buildSubmission` — so nothing about what reaches the
        server has changed. The answer-sharing tick above, which the server
        does enforce, is untouched.
      */}
      <div className={styles.submitRow}>
        {formError ? (
          <div
            className={styles.formError}
            role="alert"
            tabIndex={-1}
            ref={errorRef}
            id={ids.formError}
          >
            {formError}
          </div>
        ) : null}

        <Button type="submit" size="large" block disabled={status === 'submitting'}>
          {status === 'submitting' ? (
            <>
              <span className={styles.spinner} aria-hidden="true" />
              Sending
            </>
          ) : (
            `Send to ${profile.displayName}`
          )}
        </Button>

        <p className={styles.retention}>{profile.contactAssurance}</p>
      </div>
    </form>
  );
}

function Check() {
  return (
    <span className={styles.checkBox} aria-hidden="true">
      <svg className={styles.checkGlyph} width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path
          d="M2.5 6.2 4.8 8.5 9.5 3.8"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
